/**
 * useAnimations.js
 * Animation hooks for Premium Fleet UI
 *
 * Provides reusable animation logic for React components
 */

import {
 useState, useCallback, useRef, useEffect, useMemo 
} from 'react';

// ============================================================================
// USE ENTER ANIMATION
// Hook for enter/exit animations with render control
// ============================================================================

export const useEnterAnimation = (isVisible, duration = 200) => {
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [animationState, setAnimationState] = useState(isVisible ? 'entered' : 'exited');

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Small delay to ensure DOM is updated before animation starts
      requestAnimationFrame(() => {
        setAnimationState('entering');
        setTimeout(() => setAnimationState('entered'), duration);
      });
    } else {
      setAnimationState('exiting');
      setTimeout(() => {
        setAnimationState('exited');
        setShouldRender(false);
      }, duration);
    }
  }, [isVisible, duration]);

  return {
    shouldRender,
    animationState,
    isEntering: animationState === 'entering',
    isEntered: animationState === 'entered',
    isExiting: animationState === 'exiting',
    isExited: animationState === 'exited',
  };
};

// ============================================================================
// USE STAGGER ANIMATION
// Hook for staggered list animations
// ============================================================================

export const useStaggerAnimation = (items, staggerDelay = 50, initialDelay = 0) => {
  const [animatedItems, setAnimatedItems] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const timeoutsRef = useRef([]);

  useEffect(() => {
    // Clear previous timeouts
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    // Reset
    setAnimatedItems([]);
    setIsComplete(false);

    if (!items || items.length === 0) {
      setIsComplete(true);
      return;
    }

    // Stagger animation for each item
    items.forEach((item, index) => {
      const timeout = setTimeout(() => {
        setAnimatedItems((prev) => [...prev, item]);

        // Mark complete when last item is added
        if (index === items.length - 1) {
          setIsComplete(true);
        }
      }, initialDelay + index * staggerDelay);

      timeoutsRef.current.push(timeout);
    });

    return () => {
      timeoutsRef.current.forEach(clearTimeout);
    };
  }, [items, staggerDelay, initialDelay]);

  return {
    animatedItems,
    isComplete,
    progress: items?.length ? animatedItems.length / items.length : 1,
  };
};

// ============================================================================
// USE RIPPLE
// Hook for ripple effect on click
// ============================================================================

export const useRipple = (color = 'rgba(255, 255, 255, 0.3)') => {
  const [ripples, setRipples] = useState([]);

  const addRipple = useCallback((event) => {
    const element = event.currentTarget;
    const rect = element.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height) * 2;
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const newRipple = {
      id: Date.now() + Math.random(),
      x,
      y,
      size,
      color,
    };

    setRipples((prev) => [...prev, newRipple]);

    // Remove ripple after animation completes
    setTimeout(() => {
      setRipples((prev) => prev.filter((r) => r.id !== newRipple.id));
    }, 600);
  }, [color]);

  const clearRipples = useCallback(() => {
    setRipples([]);
  }, []);

  return {
    ripples,
    addRipple,
    clearRipples,
    rippleProps: {
      onClick: addRipple,
      style: { position: 'relative', overflow: 'hidden' },
    },
  };
};

// ============================================================================
// USE COUNT ANIMATION
// Hook for animated number counting
// ============================================================================

export const useCountAnimation = (
  targetValue,
  duration = 1000,
  options = {}
) => {
  const {
    startValue = 0,
    easing = 'easeOut', // 'linear', 'easeIn', 'easeOut', 'easeInOut'
    decimals = 0,
    delay = 0,
  } = options;

  const [currentValue, setCurrentValue] = useState(startValue);
  const [isAnimating, setIsAnimating] = useState(false);
  const startTimeRef = useRef(null);
  const rafRef = useRef(null);
  const previousTargetRef = useRef(targetValue);

  // Easing functions
  const easingFunctions = useMemo(() => ({
    linear: (t) => t,
    easeIn: (t) => t * t * t,
    easeOut: (t) => 1 - Math.pow(1 - t, 3),
    easeInOut: (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
  }), []);

  useEffect(() => {
    // Don't animate if target hasn't changed
    if (previousTargetRef.current === targetValue && currentValue === targetValue) {
      return;
    }

    const startFrom = previousTargetRef.current !== targetValue ? currentValue : startValue;
    previousTargetRef.current = targetValue;

    const delayTimeout = setTimeout(() => {
      setIsAnimating(true);
      const diff = targetValue - startFrom;

      const animate = (timestamp) => {
        if (!startTimeRef.current) {
          startTimeRef.current = timestamp;
        }

        const elapsed = timestamp - startTimeRef.current;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easingFunctions[easing](progress);

        const newValue = startFrom + diff * easedProgress;
        setCurrentValue(decimals > 0 ? parseFloat(newValue.toFixed(decimals)) : Math.round(newValue));

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
          startTimeRef.current = null;
        }
      };

      startTimeRef.current = null;
      rafRef.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      clearTimeout(delayTimeout);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [targetValue, duration, easing, decimals, delay, startValue, easingFunctions]);

  return {
    value: currentValue,
    isAnimating,
    formattedValue: currentValue.toLocaleString(),
  };
};

// ============================================================================
// USE SHAKE
// Hook for shake animation (error feedback)
// ============================================================================

export const useShake = (duration = 500) => {
  const [isShaking, setIsShaking] = useState(false);
  const timeoutRef = useRef(null);

  const shake = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsShaking(true);

    timeoutRef.current = setTimeout(() => {
      setIsShaking(false);
    }, duration);
  }, [duration]);

  const stopShake = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsShaking(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isShaking,
    shake,
    stopShake,
    shakeProps: {
      className: isShaking ? 'animate-shake' : '',
      style: isShaking
        ? { animation: `shake ${duration}ms ease-in-out` }
        : {},
    },
  };
};

// ============================================================================
// USE PULSE
// Hook for pulse animation (attention)
// ============================================================================

export const usePulse = () => {
  const [isPulsing, setIsPulsing] = useState(false);
  const intervalRef = useRef(null);

  const startPulse = useCallback(() => {
    setIsPulsing(true);
  }, []);

  const stopPulse = useCallback(() => {
    setIsPulsing(false);
  }, []);

  const togglePulse = useCallback(() => {
    setIsPulsing((prev) => !prev);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    isPulsing,
    startPulse,
    stopPulse,
    togglePulse,
    pulseProps: {
      className: isPulsing ? 'animate-pulse' : '',
    },
  };
};

// ============================================================================
// USE SCROLL REVEAL
// Hook for reveal animations on scroll
// ============================================================================

export const useScrollReveal = (options = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px',
    triggerOnce = true,
  } = options;

  const ref = useRef(null);
  const [isRevealed, setIsRevealed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsRevealed(true);
          if (triggerOnce) {
            observer.disconnect();
          }
        } else if (!triggerOnce) {
          setIsRevealed(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce]);

  return {
    ref,
    isRevealed,
  };
};

// ============================================================================
// USE HOVER ANIMATION
// Hook for hover state with animation
// ============================================================================

export const useHoverAnimation = () => {
  const [isHovered, setIsHovered] = useState(false);

  const hoverProps = useMemo(() => ({
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  }), []);

  return {
    isHovered,
    hoverProps,
    setIsHovered,
  };
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  useEnterAnimation,
  useStaggerAnimation,
  useRipple,
  useCountAnimation,
  useShake,
  usePulse,
  useScrollReveal,
  useHoverAnimation,
};
